import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft, CheckCircle2, Lock, Play, BookOpen, 
  HelpCircle, Trophy, Star, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { getCabinModule as getStaticCabinModule } from '../data/cabinCrewCurriculum';
import { fetchLevel } from '../services/curriculumService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CabinModuleDetail() {
  const { moduleId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [module, setModule] = useState(null);

  useEffect(() => {
    loadModule();
    fetchProgress();
  }, [moduleId]);

  const loadModule = async () => {
    try {
      // Önce API'den dene (cabin_crew type)
      const apiModule = await fetchLevel(moduleId, 'cabin_crew');
      if (apiModule) {
        setModule(apiModule);
        return;
      }
    } catch (error) {
      console.log('API error, falling back to static');
    }
    // Fallback: Static dosyadan yükle
    setModule(getStaticCabinModule(moduleId));
  };

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`${API}/cabin-training/progress`, { withCredentials: true });
      setProgress(response.data);
    } catch (error) {
      console.error('Error fetching progress:', error);
      setProgress({ completed_units: [], completed_lessons: [] });
    } finally {
      setLoading(false);
    }
  };

  const isUnitUnlocked = (unitIndex) => {
    // Admin için tüm üniteler açık
    if (user?.role === 'admin') return true;
    if (unitIndex === 0) return true;
    if (!progress || !module?.units) return false;
    const prevUnit = module.units[unitIndex - 1];
    return progress.completed_units?.includes(prevUnit?.unit_id);
  };

  // Modüle erişim kontrolü
  const isModuleAccessible = () => {
    if (user?.role === 'admin') return true;
    
    // Admin tarafından açılan modüller
    const unlockedModules = user?.cabin_progress?.unlocked_modules || [];
    const allUnlocked = user?.kariyer_progress?.all_unlocked || false;
    
    // Admin tüm eğitimleri açtıysa
    if (allUnlocked) return true;
    
    const moduleNum = parseInt(moduleId);
    
    // Admin bu modülü özellikle açtıysa
    if (unlockedModules.includes(moduleNum)) return true;
    
    // Modül 11-16 herkese açık
    if (moduleNum >= 11 && moduleNum <= 16) return true;
    
    // Modül 1 ve 2 herkese açık
    if (moduleNum === 1 || moduleNum === 2) return true;
    
    // Modül 3-10 için önceki modül tamamlanmış olmalı
    if (!progress) return false;
    const prevModuleId = `module_${moduleNum - 1}`;
    return progress.completed_modules?.includes(prevModuleId) || moduleNum <= (progress.current_module || 2);
  };

  const isLessonCompleted = (lessonId) => {
    return progress?.completed_lessons?.includes(lessonId);
  };

  const getUnitProgress = (unit) => {
    if (!progress || !unit?.lessons) return 0;
    const completedCount = unit.lessons.filter(l => 
      progress.completed_lessons?.includes(l.lesson_id)
    ).length;
    return Math.round((completedCount / unit.lessons.length) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin text-4xl">✈️</div>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p>Modül bulunamadı</p>
      </div>
    );
  }

  // Modüle erişim yoksa geri yönlendir
  if (!isModuleAccessible()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8">
          <Lock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Bu modül kilitli</h2>
          <p className="text-gray-500 mb-4">Bu modüle erişmek için önceki modülleri tamamlamanız gerekiyor.</p>
          <Button onClick={() => navigate('/cabin-training')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Modüllere Dön
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Header */}
      <div className={`bg-gradient-to-r ${module.color} px-4 pt-6 pb-8 rounded-b-[2rem]`}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <button 
              onClick={() => navigate('/cabin-training')}
              className="flex items-center gap-2 text-white/80 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Geri</span>
            </button>
            <span className="text-white/80 text-sm">MODÜL {module.module_id}</span>
          </div>

          <div className="text-center text-white">
            <span className="text-4xl mb-2 block">{module.icon}</span>
            <h1 className="text-2xl font-bold mb-1">{module.title}</h1>
            <p className="text-white/80 text-sm">{module.subtitle}</p>
          </div>
        </div>
      </div>

      {/* Units */}
      <div className="max-w-4xl mx-auto px-4 mt-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4">Üniteler</h2>
        
        <div className="space-y-4">
          {module.units?.map((unit, unitIndex) => {
            const unlocked = isUnitUnlocked(unitIndex);
            const unitProgress = getUnitProgress(unit);
            const isComplete = unitProgress === 100;
            
            return (
              <Card 
                key={unit.unit_id}
                className={`overflow-hidden transition-all ${
                  unlocked ? 'cursor-pointer hover:shadow-lg' : 'opacity-60'
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                      isComplete ? 'bg-green-100' : unlocked ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      {isComplete ? (
                        <CheckCircle2 className="w-7 h-7 text-green-600" />
                      ) : !unlocked ? (
                        <Lock className="w-6 h-6 text-gray-400" />
                      ) : (
                        <span className="text-2xl font-bold text-blue-600">{unitIndex + 1}</span>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900">{unit.title}</h3>
                      <p className="text-sm text-gray-500">{unit.description}</p>
                      
                      {unlocked && unitProgress > 0 && unitProgress < 100 && (
                        <div className="mt-2">
                          <Progress value={unitProgress} className="h-1.5" />
                          <span className="text-xs text-gray-400 mt-1">{unitProgress}%</span>
                        </div>
                      )}
                    </div>
                    
                    {unlocked && (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                  </div>

                  {/* Lessons */}
                  {unlocked && (
                    <div className="mt-4 pl-4 border-l-2 border-gray-100 space-y-2">
                      {unit.lessons?.map((lesson, lessonIndex) => {
                        const completed = isLessonCompleted(lesson.lesson_id);
                        return (
                          <div 
                            key={lesson.lesson_id}
                            onClick={() => navigate(`/cabin-training/lesson/${lesson.lesson_id}`)}
                            className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                              completed ? 'bg-green-50' : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              completed ? 'bg-green-500' : 'bg-gray-200'
                            }`}>
                              {completed ? (
                                <CheckCircle2 className="w-4 h-4 text-white" />
                              ) : lesson.type === 'scenario' ? (
                                <HelpCircle className="w-4 h-4 text-gray-500" />
                              ) : lesson.type === 'quiz' || lesson.type === 'exam' ? (
                                <Trophy className="w-4 h-4 text-gray-500" />
                              ) : (
                                <BookOpen className="w-4 h-4 text-gray-500" />
                              )}
                            </div>
                            <div className="flex-1">
                              <p className={`text-sm font-medium ${completed ? 'text-green-700' : 'text-gray-700'}`}>
                                {lesson.title}
                              </p>
                              <p className="text-xs text-gray-400">{lesson.duration} dk · +{lesson.xp} Mil</p>
                            </div>
                            {!completed && (
                              <Play className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
